import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';

/**
 * Gizlilik Politikası sayfası
 * @returns {JSX.Element} Gizlilik Politikası sayfası
 */
const PrivacyPolicy = () => {
  const { t } = useTranslation();
  
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/calculator">
                <Button variant="ghost" size="sm" className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  {t('common.back')}
                </Button>
              </Link>
              <h1 className="text-3xl font-bold">{t('privacy.title')}</h1>
            </div>
          </div>
        </div>
      </header>
      
      <main className="flex-1 container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          
          {/* Giriş Bilgileri */}
          <Card>
            <CardHeader>
              <CardTitle>{t('privacy.introduction_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                {t('privacy.introduction_content')}
              </p>
            </CardContent>
          </Card>

          {/* Veri Toplama */}
          <Card>
            <CardHeader>
              <CardTitle>{t('privacy.data_collection_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  {t('privacy.data_collection_content')}
                </p>
                
                <h4 className="font-semibold text-base">{t('privacy.collected_data_title')}</h4>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li>{t('privacy.collected_data_login')}</li>
                  <li>{t('privacy.collected_data_calculations')}</li>
                  <li>{t('privacy.collected_data_usage')}</li>
                  <li>{t('privacy.collected_data_technical')}</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Veri Kullanımı */}
          <Card>
            <CardHeader>
              <CardTitle>{t('privacy.data_usage_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  {t('privacy.data_usage_content')}
                </p>
                
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li>{t('privacy.usage_service')}</li>
                  <li>{t('privacy.usage_security')}</li>
                  <li>{t('privacy.usage_improvement')}</li>
                  <li>{t('privacy.usage_support')}</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Veri Güvenliği */}
          <Card>
            <CardHeader>
              <CardTitle>{t('privacy.data_security_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                {t('privacy.data_security_content')}
              </p>
            </CardContent>
          </Card>

          {/* Çerezler */}
          <Card>
            <CardHeader>
              <CardTitle>{t('privacy.cookies_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  {t('privacy.cookies_content')}
                </p>
                
                <h4 className="font-semibold text-base">{t('privacy.cookie_types_title')}</h4>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li>{t('privacy.cookie_essential')}</li>
                  <li>{t('privacy.cookie_preferences')}</li>
                  <li>{t('privacy.cookie_analytics')}</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Kullanıcı Hakları */}
          <Card>
            <CardHeader>
              <CardTitle>{t('privacy.user_rights_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  {t('privacy.user_rights_content')}
                </p>
                
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li>{t('privacy.right_access')}</li>
                  <li>{t('privacy.right_correction')}</li>
                  <li>{t('privacy.right_deletion')}</li>
                  <li>{t('privacy.right_portability')}</li>
                  <li>{t('privacy.right_object')}</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Üçüncü Taraflar */}
          <Card>
            <CardHeader>
              <CardTitle>{t('privacy.third_parties_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                {t('privacy.third_parties_content')}
              </p>
            </CardContent>
          </Card>

          {/* İletişim */}
          <Card>
            <CardHeader>
              <CardTitle>{t('privacy.contact_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  {t('privacy.contact_content')}
                </p>
                
                <div className="bg-muted p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">ADC Makine Sanayi ve Ticaret Limited Şirketi</h4>
                  <p className="text-sm text-muted-foreground">
                    E-posta: info@alasfire.com<br />
                    Website: www.alasfire.com
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Güncellemeler */}
          <Card>
            <CardHeader>
              <CardTitle>{t('privacy.updates_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                {t('privacy.updates_content')}
              </p>
            </CardContent>
          </Card>

          <Separator />
          
          {/* Son Güncelleme */}
          <div className="text-center text-sm text-muted-foreground">
            <p>{t('privacy.last_updated')}: {new Date().toLocaleDateString('tr-TR')}</p>
          </div>
        </div>
      </main>
      
      <footer className="py-6 border-t">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-center text-muted-foreground">
            © {new Date().getFullYear()} ADC Makine Sanayi ve Ticaret Limited Şirketi. {t('app.footer_text')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy; 